import React, { useEffect, useMemo, useState } from "react";
import { Input, Select, Space, Collapse, Alert, message, Button } from "antd";
import { getCountryCallingCode } from "libphonenumber-js";
import { useQuery } from "@tanstack/react-query";
import ResponsiveModal from "components/shared-components/ResponsiveModal";
import apiClient from "services/apiClient";
import { useMyContext } from "Context/MyContextProvider";
import { useInternationalGateway } from "../Hooks/useInternationalGateway";
import { iso2ToFlagEmoji } from "utils/phoneCountry";

const dialCodeForIso2 = (iso2) => {
  try {
    return `+${getCountryCallingCode(iso2)}`;
  } catch {
    return "";
  }
};

const buildOptionsFromPricingCountries = (pricingCountries) => {
  const options = (pricingCountries || [])
    .map((row) => {
      const iso2 = String(row?.country_iso2 || "").toUpperCase();
      if (!iso2) {
        return null;
      }

      const name = row?.country_name || iso2;
      const dialCode = dialCodeForIso2(iso2);

      return {
        value: iso2,
        dialCode,
        label: `${iso2ToFlagEmoji(iso2)} ${name}${dialCode ? ` (${dialCode})` : ""}`,
        search: `${name} ${iso2} ${dialCode}`.toLowerCase(),
      };
    })
    .filter(Boolean);

  // Ensure India is always present for domestic sending.
  if (!options.some((option) => option.value === "IN")) {
    options.unshift({
      value: "IN",
      dialCode: "+91",
      label: `${iso2ToFlagEmoji("IN")} India (+91)`,
      search: "india in 91",
    });
  }

  return options.sort((a, b) => {
    if (a.value === "IN") return -1;
    if (b.value === "IN") return 1;
    return a.label.localeCompare(b.label);
  });
};

const INDIA_ONLY_OPTIONS = [
  {
    value: "IN",
    dialCode: "+91",
    label: `${iso2ToFlagEmoji("IN")} India (+91)`,
    search: "india in 91",
  },
];

const PresetTest = ({ open, onClose, presetId, presetType }) => {
  const { UserData, api, authToken } = useMyContext();
  const userId = UserData?.id;
  const { gatewayEnabled, gatewayLoading } = useInternationalGateway({
    api,
    authToken,
    userId,
  });

  const [number, setNumber] = useState("");
  const [country, setCountry] = useState("IN");
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const countryLockedToIndia = !gatewayEnabled;

  const { data: pricingCountries = [], isLoading: countriesLoading } = useQuery({
    queryKey: ["preset-test-pricing-countries", userId],
    queryFn: async () => {
      const response = await apiClient.get(`${api}user-pricing/${userId}/countries`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      return response.data?.data ?? [];
    },
    enabled: !!userId && open && gatewayEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const countryOptions = useMemo(() => {
    if (countryLockedToIndia) {
      return INDIA_ONLY_OPTIONS;
    }

    return buildOptionsFromPricingCountries(pricingCountries);
  }, [countryLockedToIndia, pricingCountries]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setNumber("");
    setSubmitError("");
    setCountry("IN");
  }, [open]);

  useEffect(() => {
    if (countryLockedToIndia) {
      if (country !== "IN") {
        setCountry("IN");
      }
      return;
    }

    const stillValid = countryOptions.some((option) => option.value === country);
    if (!stillValid) {
      setCountry(countryOptions[0]?.value || "IN");
    }
  }, [countryLockedToIndia, country, countryOptions]);

  const selectedCountry = useMemo(
    () => countryOptions.find((option) => option.value === country) || countryOptions[0],
    [country, countryOptions]
  );

  const handleOk = async () => {
    const trimmedNumber = String(number || "").trim();
    if (!trimmedNumber) {
      setSubmitError("Please enter a WhatsApp number.");
      return;
    }
    if (!presetId || !presetType) {
      setSubmitError("No preset selected.");
      return;
    }

    setLoading(true);
    setSubmitError("");

    try {
      const response = await apiClient.post("messaging/send-preset", {
        number: trimmedNumber,
        country: countryLockedToIndia ? "IN" : country,
        preset_id: presetId,
        preset_type: presetType,
      });

      if (!response?.data?.ok) {
        setSubmitError(response?.data?.error || "Failed to send message.");
        return;
      }

      setNumber("");
      message.success(response?.data?.message || "Message sent successfully");
      onClose();
    } catch (error) {
      let errMsg = "Failed to send message. Please try again.";
      const data = error?.response?.data;
      if (data) {
        if (typeof data === "string") {
          errMsg = data;
        } else if (data?.error && typeof data.error === "string") {
          errMsg = data.error;
        } else if (data?.error?.message) {
          errMsg = data.error.message;
        } else if (data?.message) {
          errMsg = data.message;
        } else if (data?.errors) {
          const firstField = Object.values(data.errors)[0];
          if (Array.isArray(firstField) && firstField[0]) {
            errMsg = firstField[0];
          }
        }
      }
      setSubmitError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setNumber("");
    setSubmitError("");
    onClose();
  };

  const selectLoading = gatewayLoading || (gatewayEnabled && countriesLoading);

  const footer = [
    <Button key="cancel" onClick={handleCancel} disabled={loading}>
      Cancel
    </Button>,
    <Button
      key="submit"
      type="primary"
      onClick={handleOk}
      loading={loading}
      disabled={selectLoading || !presetId}
    >
      Send
    </Button>
  ];

  return (
    <ResponsiveModal
      title="Enter Your WhatsApp Number"
      open={open}
      onClose={handleCancel}
      footer={footer}
    >
      {submitError && (
        <Alert
          message={submitError}
          type="error"
          showIcon
          style={{ marginBottom: "16px" }}
        />
      )}

      <Space.Compact style={{ width: "100%" }}>
        <Select
          showSearch
          value={country}
          disabled={countryLockedToIndia || selectLoading || loading}
          loading={selectLoading}
          onChange={setCountry}
          style={{ width: 200 }}
          optionFilterProp="label"
          filterOption={(input, option) =>
            String(option?.search || option?.label || "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          options={countryOptions.map((option) => ({
            value: option.value,
            label: option.label,
            search: option.search,
          }))}
        />
        <Input
          value={number}
          id="numberInput"
          onChange={(e) => setNumber(e.target.value)}
          placeholder={`Enter number without ${selectedCountry?.dialCode || "+91"}`}
          autoFocus
          disabled={loading}
        />
      </Space.Compact>

      {countryLockedToIndia && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="International gateway is disabled. Messages can only be sent to India (+91)."
        />
      )}

      <div style={{ marginTop: "16px" }}>
        <Collapse defaultActiveKey={["1"]}>
          <Collapse.Panel header="Important Information" key="1">
            <ul style={{ paddingLeft: "20px", margin: 0 }}>
              <li>
                Send a message to <b>{UserData?.whatsapp_number}</b> first from
                your WhatsApp application to ensure immediate message delivery.
              </li>
              {presetType === "list" && (
                <li>
                  If there are no messages exchanged with{" "}
                  <b>{UserData?.whatsapp_number}</b> in the last 24 hours, list
                  presets may not send (customer service window required).
                </li>
              )}
              <li>
                Enter the national number only — the selected country prefix is
                applied automatically.
              </li>
              <li>
                Ensure the recipient has opted in to receive messages from your
                business account.
              </li>
            </ul>
          </Collapse.Panel>
        </Collapse>
      </div>
    </ResponsiveModal>
  );
};

export default PresetTest;
